import React, { useState, useEffect } from 'react';
import { Upload, ChevronDown, Plus, Edit, Trash2, X, Package, Search, FileText, LogOut } from 'lucide-react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import api from '../api/baseapi';
import { generateBarcode } from '../utils/barcodeGenerator.js';
import { calculateMonthsAndDays } from '../utils/dateCalculator.js';
import PizZip from 'pizzip';
import Docxtemplater from 'docxtemplater';
import { processPatientName } from '../utils/prenameUtils.js';
import { saveAs } from 'file-saver';
import { generateMonthlyMedicationWord } from '../services/Monthlymedicationwordservice';
import { generatePhysicianOrderSheet} from '../services/physicianOrderSheetWordService';

const formatThaiDate = (date) => {
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = (date.getFullYear() + 543).toString();
  return `${day}/${month}/${year}`;
};

// ============================================================
// ✅ Admission Medication Reconciliation — Web version
//    (ย้ายมาจาก admissionMedicationReconciliationWordService.js)
// ============================================================

const formatMealTiming = (scheduleTimeDisplay, timing) => {
  if (
    !scheduleTimeDisplay ||
    timing === 'prn' ||
    timing === 'stat' ||
    timing === 'S.O.S'
  ) {
    return '';
  }

  const timeMapping = {
    '07:30': 'เช้า', '08:00': 'เช้า', '08:30': 'เช้า',
    '11:30': 'กลางวัน', '12:00': 'กลางวัน', '12:30': 'กลางวัน',
    '17:30': 'เย็น', '18:00': 'เย็น', '18:30': 'เย็น',
    '21:00': 'ก่อนนอน',
  };

  const times = scheduleTimeDisplay.split(',').map(t => t.trim());
  const mealTimes = [];

  times.forEach(time => {
    const cleanTime = time.replace('ก่อนอาหาร', '').replace('หลังอาหาร', '').trim();
    mealTimes.push(timeMapping[cleanTime] || cleanTime);
  });

  const uniqueMealTimes = [...new Set(mealTimes)];

  if (timing === 'hs') return 'ก่อนนอน';

  let prefix = '';
  if (timing === 'ac') prefix = 'ก่อนอาหาร';
  else if (timing === 'pc') prefix = 'หลังอาหาร';

  if (prefix && uniqueMealTimes.length > 0) {
    return `${prefix} ${uniqueMealTimes.join(' ')}`;
  }

  return uniqueMealTimes.join(' ');
};


const generateMedicationRows = (medications) => {
  const totalRows = 20;
  let rows = '';

  for (let i = 0; i < totalRows; i++) {
    const med = medications && medications[i] ? medications[i] : null;

    if (med) {
      const medicationName = med.medicationName || med.genericName || med.tradeName || '';
      const genericName = med.genericName || '';
      const tradeName = med.tradeName || '';
      const dosageInfo = `${med.dosage || ''} ${med.route || ''}`.trim();
      const instruction = med.dosageInstruction || med.frequency || '';
      const lastDose = med.lastDose || '';
      const externalSource = med.isExternal ? (med.externalHospital || '') : '';
      const quantity = med.quantity ? `จำนวน: ${med.quantity}` : '';
      const specialInstruction = med.specialInstruction ? `(${med.specialInstruction})` : '';
      const scheduleTimeDisplay = formatMealTiming(
        med.scheduleTimeDisplay || med.schedule_time_display,
        med.timing
      );
      const prnCondition = med.prnCondition ? `PRN: ${med.prnCondition}` : '';
      const timingDisplay =
        med.timing === 'prn' || med.timing === 'stat' || med.timing === 'S.O.S' ||
          med.frequency === 'prn' || med.frequency === 'stat'
          ? 'เมื่อมีอาการ/ตามคำสั่งแพทย์'
          : scheduleTimeDisplay;

      const continueSame = med.status === 'continue_same' ? '☑' : '☐';
      const continueAdjusted = med.status === 'continue_adjust' ? '☑' : '☐';
      const hold = med.status === 'pause' ? '☑' : '☐';
      const discontinue = med.status === 'discontinue' ? '☑' : '☐';
      const adjustedDosageText =
        med.status === 'continue_adjust' && med.adjustedDosage ? med.adjustedDosage : '';
      const changeReason = med.changeReason || '';

      rows += `
        <tr>
          <td style="text-align:center;font-weight:bold;background-color:#F5F5F5">${i + 1}</td>
          <td style="text-align:left;padding:4px 6px">
            <div style="font-weight:bold;margin-bottom:2px;font-size:11pt">${medicationName}</div>
            ${genericName && genericName !== medicationName ? `<div style="font-size:11pt;color:#666">ชื่อสามัญ: ${genericName}</div>` : ''}
            ${tradeName && tradeName !== medicationName ? `<div style="font-size:11pt;color:#666;font-style:italic">(${tradeName})</div>` : ''}
            ${dosageInfo ? `<div style="font-size:11pt;color:#666">ขนาด: ${dosageInfo}</div>` : ''}
            ${instruction ? `<div style="font-size:11pt;color:#666">วิธีใช้: ${instruction}</div>` : ''}
            ${timingDisplay ? `<div style="font-size:11pt;color:#666">เวลา: ${timingDisplay}</div>` : ''}
            ${prnCondition ? `<div style="font-size:11pt;color:#E65100;font-weight:bold">${prnCondition}</div>` : ''}
            ${quantity ? `<div style="font-size:11pt;color:#666">${quantity}</div>` : ''}
            ${specialInstruction ? `<div style="font-size:11pt;color:#E65100">${specialInstruction}</div>` : ''}
          </td>
          <td style="text-align:center;font-size:11pt">${lastDose}</td>
          <td style="text-align:left;font-size:11pt;padding:3px">${externalSource}</td>
          <td style="text-align:center;font-size:14pt">${continueSame}</td>
          <td style="text-align:left;padding:3px;font-size:11pt">
            <div style="text-align:center;font-size:14pt;margin-bottom:2px">${continueAdjusted}</div>
            ${adjustedDosageText ? `<div style="font-size:11pt">${adjustedDosageText}</div>` : ''}
          </td>
          <td style="text-align:center;font-size:14pt">${hold}</td>
          <td style="text-align:center;font-size:14pt">${discontinue}</td>
          <td style="text-align:left;font-size:11pt;padding:3px">${changeReason}</td>
        </tr>`;
    } else {
      rows += `
        <tr>
          <td style="text-align:center;font-weight:bold;background-color:#F5F5F5">${i + 1}</td>
          <td style="min-height:40px">&nbsp;</td>
          <td>&nbsp;</td>
          <td>&nbsp;</td>
          <td style="text-align:center;font-size:14pt">☐</td>
          <td style="text-align:left;padding:3px"><div style="text-align:center;font-size:14pt">☐</div></td>
          <td style="text-align:center;font-size:14pt">☐</td>
          <td style="text-align:center;font-size:14pt">☐</td>
          <td>&nbsp;</td>
        </tr>`;
    }
  }

  return rows;
};

const generateAdmissionMedHTMLContent = (patient) => {
  const currentDate = new Date().toLocaleDateString('th-TH', {
    year: 'numeric', month: 'long', day: 'numeric',
  });

  const registrationDate = patient.registration_date
    ? new Date(patient.registration_date).toLocaleDateString('th-TH', {
      year: 'numeric', month: 'long', day: 'numeric',
    })
    : '-';

  return `
<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
<head>
  <meta charset="utf-8">
  <title>Admission Medication Reconciliation</title>
  <!--[if gte mso 9]><xml><w:WordDocument><w:View>Print</w:View><w:Zoom>100</w:Zoom><w:DoNotOptimizeForBrowser/></w:WordDocument></xml><![endif]-->
  <style>
    @page { size: A4 portrait; margin: 0.4in; }
    @page Section1 { size: 8.3in 11.7in; margin: 0.4in; }
    div.Section1 { page: Section1; width: 100%; }
    * { box-sizing: border-box; }
    body { font-family: 'TH SarabunPSK','Cordia New','Angsana New',sans-serif; font-size:16pt; margin:0; padding:0; width:100%; }
    .header { text-align:center; margin-bottom:10px; border-bottom:3px solid #4A90E2; padding-bottom:8px; }
    .header h1 { font-size:20pt; font-weight:bold; margin:3px 0; color:#2C3E50; }
    .patient-info { margin-bottom:10px; background-color:#F8F9FA; padding:8px 10px; border-radius:6px; border-left:4px solid #4A90E2; }
    .patient-info-row { margin-bottom:4px; font-size:12pt; }
    .patient-info-label { font-weight:bold; color:#555; display:inline-block; min-width:100px; }
    .patient-info-value { color:#333; }
    table { width:100% !important; border-collapse:collapse; margin-bottom:12px; table-layout:fixed !important; }
    th, td { border:1px solid #333; padding:4px 3px; text-align:center; vertical-align:middle; font-size:10pt; word-wrap:break-word; }
    th { background-color:#4A90E2; color:white; font-weight:bold; font-size:10pt; padding:6px 3px; }
    .col-no { width:5%; } .col-medication { width:30%; } .col-last-dose { width:10%; }
    .col-external { width:15%; } .col-continue { width:8%; } .col-adjusted { width:12%; }
    .col-hold { width:7%; } .col-discontinue { width:8%; } .col-reason { width:15%; }
  </style>
</head>
<body>
  <div class="Section1">
    <div class="header"><h1>Admission Medication Reconciliation</h1></div>
    <div class="patient-info">
      <div class="patient-info-row">
        <span class="patient-info-label">ชื่อ-สกุลผู้ป่วย:</span>
        <span class="patient-info-value">${patient.patient_name || '-'}</span>
        &nbsp;&nbsp;&nbsp;
        <span class="patient-info-label">HN:</span>
        <span class="patient-info-value">${patient.hn || '-'}</span>
      </div>
      <div class="patient-info-row">
        <span class="patient-info-label">อายุ:</span>
        <span class="patient-info-value">${patient.age ? `${patient.age} ปี` : '-'}</span>
        &nbsp;&nbsp;&nbsp;
        <span class="patient-info-label">ห้อง:</span>
        <span class="patient-info-value">${patient.room_number || '-'}</span>
        &nbsp;&nbsp;&nbsp;
        <span class="patient-info-label">วอร์ด:</span>
        <span class="patient-info-value">${patient.ward_name || '-'}</span>
      </div>
      <div class="patient-info-row">
        <span class="patient-info-label">วันที่รับเข้า:</span>
        <span class="patient-info-value">${registrationDate}</span>
        &nbsp;&nbsp;&nbsp;
        <span class="patient-info-label">วันที่บันทึก:</span>
        <span class="patient-info-value">${currentDate}</span>
      </div>
    </div>
    <table>
      <thead>
        <tr>
          <th class="col-no" rowspan="2">ลำดับ</th>
          <th class="col-medication" rowspan="2">ชื่อยา / ขนาด / วิธีใช้</th>
          <th class="col-last-dose" rowspan="2">มื้อสุดท้าย</th>
          <th class="col-external" rowspan="2">กรณียานอก รพ.<br/>ระบุแหล่ง</th>
          <th colspan="4">การดำเนินการ</th>
          <th class="col-reason" rowspan="2">เหตุผลที่เปลี่ยนแปลง<br/>การใช้ยา</th>
        </tr>
        <tr>
          <th class="col-continue">ใช้ต่อ<br/>ขนาดเดิม</th>
          <th class="col-adjusted">ใช้ต่อ<br/>ปรับขนาดเป็น</th>
          <th class="col-hold">พักใช้<br/>(Hold)</th>
          <th class="col-discontinue">ไม่ใช้ต่อ</th>
        </tr>
      </thead>
      <tbody>${generateMedicationRows(patient.medications)}</tbody>
    </table>
  </div>
</body>
</html>`;
};

/**
 * ✅ generateAdmissionMedicationReconciliation — Web version
 *
 * สร้างและดาวน์โหลดไฟล์ .doc (HTML-in-Word) ตรงในเบราว์เซอร์
 * ไม่ต้องใช้ expo-file-system / expo-sharing
 *
 * @param {object} patient  — ข้อมูลผู้ป่วย เหมือนเดิมทุก field
 */
const generateAdmissionMedicationReconciliation = (patient) => {
  try {
    const htmlContent = generateAdmissionMedHTMLContent(patient);

    // สร้าง Blob จาก HTML string (Word เปิด .doc ที่เป็น HTML ได้)
    const blob = new Blob(['\ufeff', htmlContent], {
      type: 'application/msword;charset=utf-8',
    });

    const safeName = (patient.patient_name || 'patient').replace(/\s+/g, '_');
    const filename = `Admission_Med_Reconciliation_${safeName}_${Date.now()}.doc`;

    // ดาวน์โหลดผ่าน <a> tag — ไม่ต้องการ library เพิ่ม
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    console.log('✅ Admission Medication Reconciliation downloaded:', filename);
  } catch (error) {
    console.error('❌ Error generating Admission Med Reconciliation:', error);
    throw new Error('ไม่สามารถสร้างเอกสารได้: ' + error.message);
  }
};

// ============================================================
// EditVN Component
// ============================================================

export default function EditVN() {
  const navigate = useNavigate();
  const { vnId } = useParams();
  const location = useLocation();
  const vnFromState = location.state?.vnId || vnId;

  const [error, setError] = useState(null);

  // Discharge states
  const [discharged, setDischarged] = useState(false);
  const [dischargeLoading, setDischargeLoading] = useState(false);
  const [showDischargeModal, setShowDischargeModal] = useState(false);
  const [dischargeDate, setDischargeDate] = useState('');
  const [dischargeNotes, setDischargeNotes] = useState('');

  const [formData, setFormData] = useState({
    hn: '',
    an: '',
    prename: '',
    firstName: '',
    lastName: '',
    idNumber: '',
    clinicType: 'STROKE',
    date: '',
    toDate: '',
    building: '',
    floor: 'รายวัน',
    price: '',
    birth_date: '',
    age: '',
    address: '',
    village: '',
    subDistrict: '',
    district: '',
    province: '',
    phone: '',
    email: '',
    lineId: '',
    relationship: '',
    authorizedPerson: '',
    authorizedIdCard: '',
    patientName: '',
    patientAge: '',
    patientIdCard: '',
    startDate: '',
    endDate: '',
    totalMonths: '',
    totalDays: '',
    roomType: '',
    roomNumber: '',
    serviceRate: '',
    nursingRate: '',
    medicalSupplies: '',
    doctorVisitRate: '',
    initialExamFee: '',
    totalServiceFee: '',
    extraDoctorVisitFee: '',
    gender: '',
    finalGender: '',
  });

  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [patient_id, setPatient_id] = useState(null);
  const [service_id, setService_id] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(true);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);

  // Table data
  const [packageData, setPackageData] = useState([]);
  const [medicalData, setMedicalData] = useState([]);
  const [contractData, setContractData] = useState([]);

  const [modalForm, setModalForm] = useState({ name: '', category: '', price: '' });
  const [serviceData, setServiceData] = useState(null);

  // Search states
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [rooms, setRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(true);
  const [selectedRoom, setSelectedRoom] = useState(null);

  // Package/Medical/Contract modals
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [availablePackages, setAvailablePackages] = useState([]);
  const [selectedPackageForPatient, setSelectedPackageForPatient] = useState(null);

  const [showMedicalModal, setShowMedicalModal] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [availableMedical, setAvailableMedical] = useState([]);
  const [availableContracts, setAvailableContracts] = useState([]);
  const [selectedMedical, setSelectedMedical] = useState(null);
  const [selectedContract, setSelectedContract] = useState(null);

  const fetchServiceRegistration = async (vnId) => {
    try {
      setLoading(true);
      setError(null);

      const response = await api.get(`/service-registrations/${vnId}`);
      const responseMed = await api.get(`medication-reconciliation/${vnId}`)
      const resultMed = responseMed.data.data;
      console.log('ยา', resultMed)
      const result = response.data;
      if (!result.success) throw new Error(result.error || 'Failed to fetch service registration');
      console.log('555', result)
      const serviceReg = result.data;
      setService_id(serviceReg.registration_id);
      setPatient_id(serviceReg.patient_id);
      setDischarged(serviceReg.status === 'discharged');
      setServiceData(resultMed); // ✅ เพิ่มบรรทัดนี้

      let patientAge = '';
      let patientBirthDate = '';
      if (serviceReg.patient_id) {
        try {
          const patientResult = await api.get(`/patients/${serviceReg.patient_id}`);
          if (patientResult.data.success && patientResult.data.data) {
            patientAge = patientResult.data.data.age ? patientResult.data.data.age.toString() : '';
            patientBirthDate = patientResult.data.data.birth_date
              ? patientResult.data.data.birth_date.split('T')[0]
              : '';
          }
        } catch (err) {
          console.error('Error fetching patient for age:', err);
        }
      }

      setFormData(prev => ({
        ...prev,
        hn: serviceReg.hn || '',
        prename: serviceReg.prename || '',
        firstName: serviceReg.first_name || '',
        lastName: serviceReg.last_name || '',
        idNumber: serviceReg.id_card || '',
        an: serviceReg.service_number || '',
        gender: serviceReg.gender || '',
        birth_date: patientBirthDate || serviceReg.birth_date ? serviceReg.birth_date.split('T')[0] : '',
        age: patientAge || (serviceReg.age ? serviceReg.age.toString() : ''),
        date: serviceReg.contract_start_date ? serviceReg.contract_start_date.split('T')[0] : '',
        toDate: serviceReg.contract_end_date ? serviceReg.contract_end_date.split('T')[0] : '',
        building: serviceReg.room_number || '',
        type: serviceReg.room_type || '',
        floor: serviceReg.billing_type || 'รายสัปดาห์',
        price: serviceReg.base_price?.toString() || '',
      }));

      setSelectedRoom({
        room_number: serviceReg.room_number || '',
        room_type: serviceReg.room_type || '',
      });

      if (serviceReg.contract) {
        if (serviceReg.contract.packages && Array.isArray(serviceReg.contract.packages)) {
          setPackageData(serviceReg.contract.packages.map(pkg => ({
            id: Date.now() + Math.random(),
            packageId: pkg.package_id || pkg.id,
            name: pkg.package_name || pkg.name,
            price: parseInt(pkg.final_price) || 0,
            originalPrice: parseInt(pkg.original_price) || 0,
            discount: { type: pkg.discount_type || null, value: parseInt(pkg.discount_value) || 0 },
          })));
        }
        if (serviceReg.contract.medical_supplies && Array.isArray(serviceReg.contract.medical_supplies)) {
          setMedicalData(serviceReg.contract.medical_supplies.map(med => ({
            id: Date.now() + Math.random(),
            medicalId: med.medical_supply_id || med.id,
            name: med.item_name || med.name,
            price: parseInt(med.final_price) || 0,
            originalPrice: parseInt(med.original_price) || 0,
            discount: { type: med.discount_type || null, value: parseInt(med.discount_value) || 0 },
          })));
        }
        if (serviceReg.contract.contract_items && Array.isArray(serviceReg.contract.contract_items)) {
          setContractData(serviceReg.contract.contract_items.map(con => ({
            id: Date.now() + Math.random(),
            contractId: con.contract_item_id || con.id,
            name: con.item_name || con.name,
            category: con.category || '',
            price: parseInt(con.final_price) || 0,
            originalPrice: parseInt(con.original_price) || 0,
            discount: { type: con.discount_type || null, value: parseInt(con.discount_value) || 0 },
          })));
        }
      } else {
        setPackageData([]);
        setMedicalData([]);
        setContractData([]);
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'ไม่สามารถโหลดข้อมูล VN ได้';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDischarge = async () => {
    try {
      setDischargeLoading(true);
      setError(null);
      if (!dischargeDate) { setError('กรุณาเลือกวันที่จำหน่าย'); return; }

      const response = await api.patch(`/service-registrations/${service_id}/discharge`, {
        discharge_date: dischargeDate,
        discharge_notes: dischargeNotes || null,
      });
      const result = response.data;
      if (!result.success) throw new Error(result.error || 'ไม่สามารถจำหน่ายผู้ป่วยได้');

      setDischarged(true);
      setFormData(prev => ({ ...prev, toDate: dischargeDate }));
      setShowDischargeModal(false);
      setDischargeDate('');
      setDischargeNotes('');
      alert('✅ จำหน่ายผู้ป่วยสำเร็จ!');
      await fetchServiceRegistration(vnFromState);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'เกิดข้อผิดพลาดในการจำหน่ายผู้ป่วย');
    } finally {
      setDischargeLoading(false);
    }
  };

  const handleUndischarge = async () => {
    try {
      setDischargeLoading(true);
      setError(null);
      const response = await api.patch(`/service-registrations/${service_id}/undischarge`);
      const result = response.data;
      if (!result.success) throw new Error(result.error || 'ไม่สามารถยกเลิกการจำหน่ายได้');

      setDischarged(false);
      setFormData(prev => ({ ...prev, toDate: '' }));
      alert('✅ ยกเลิกการจำหน่ายสำเร็จ!');
      await fetchServiceRegistration(vnFromState);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'เกิดข้อผิดพลาดในการยกเลิกการจำหน่าย');
    } finally {
      setDischargeLoading(false);
    }
  };

  const handleUniversalSearch = async () => {
    if (!searchQuery || searchQuery.trim().length < 2) {
      setError('กรุณากรอกข้อมูลอย่างน้อย 2 ตัวอักษร');
      return;
    }
    try {
      setIsSearching(true);
      setError(null);
      const response = await api.get(`/patients/search`, { params: { q: searchQuery.trim() } });
      const results = response.data;
      if (results.data && results.data.length > 0) {
        setSearchResults(results.data);
        setShowSearchModal(true);
      } else {
        setSearchResults([]);
        setError('ไม่พบข้อมูลผู้ป่วย');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'เกิดข้อผิดพลาดในการค้นหา');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const selectPatientFromSearch = (patient) => {
    setFormData(prev => ({
      ...prev,
      hn: patient.hn || '',
      firstName: patient.first_name || patient.firstName || '',
      lastName: patient.last_name || patient.lastName || '',
      idNumber: patient.id_card || patient.idNumber || '',
      birth_date: patient.birth_date ? patient.birth_date.split('T')[0] : '',
      age: patient.age ? patient.age.toString() : '',
      gender: patient.gender || '',
    }));
    setPatient_id(patient.id);
    if (patient.profile_image) setPreviewUrl(patient.profile_image);
    setShowSearchModal(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const fetchDepartments = async () => {
    try {
      setDepartmentsLoading(true);
      const response = await api.get(`/departments`);
      const data = response.data;
      if (data.success && data.data) setDepartments(data.data.filter(dept => dept.is_active));
    } catch (err) {
      setError(`เกิดข้อผิดพลาดในการดึงข้อมูลแผนก: ${err.response?.data?.error || err.message}`);
    } finally {
      setDepartmentsLoading(false);
    }
  };

  const fetchRooms = async () => {
    try {
      setRoomsLoading(true);
      const response = await api.get(`/room`, { params: { active: true } });
      const data = response.data;
      if (data.success && data.data) {
        setRooms(data.data.sort((a, b) => (a.display_order || 0) - (b.display_order || 0)));
      }
    } catch (err) {
      setError(`เกิดข้อผิดพลาดในการดึงข้อมูลห้อง: ${err.response?.data?.error || err.message}`);
    } finally {
      setRoomsLoading(false);
    }
  };

  const fetchAvailablePackages = async () => {
    try {
      const response = await api.get(`/packages`, { params: { active: true } });
      if (response.data.success) setAvailablePackages(response.data.data);
    } catch (error) { console.error('❌ Error fetching packages:', error); }
  };

  const fetchAvailableMedical = async () => {
    try {
      const response = await api.get(`/medical`, { params: { active: true } });
      if (response.data.success) setAvailableMedical(response.data.data);
    } catch (error) { console.error('❌ Error fetching medical:', error); }
  };

  const fetchAvailableContracts = async () => {
    try {
      const response = await api.get(`/contracts`, { params: { active: true } });
      if (response.data.success) setAvailableContracts(response.data.data);
    } catch (error) { console.error('❌ Error fetching contracts:', error); }
  };

  useEffect(() => {
    fetchDepartments();
    fetchRooms();
    fetchAvailablePackages();
    fetchAvailableMedical();
    fetchAvailableContracts();
  }, []);

  useEffect(() => {
    if (vnFromState) fetchServiceRegistration(vnFromState);
  }, [vnFromState]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRoomChange = (e) => {
    const roomId = e.target.value;
    const room = rooms.find(r => r.room_number === roomId);
    if (room) {
      setSelectedRoom(room);
      setFormData(prev => ({
        ...prev,
        building: room.room_number,
        roomTypeId: room.id,
        floor: 'รายวัน',
        price: (room.daily_price || 0).toString(),
      }));
    } else {
      setSelectedRoom(null);
      setFormData(prev => ({ ...prev, building: '', roomTypeId: null, floor: 'รายวัน', price: '' }));
    }
  };

  const handleBillingTypeChange = (e) => {
    const billingType = e.target.value;
    setFormData(prev => ({ ...prev, floor: billingType }));
    if (selectedRoom) {
      const price =
        billingType === 'รายวัน' ? selectedRoom.daily_price || 0
          : billingType === 'รายสัปดาห์' ? selectedRoom.weekly_price || 0
            : billingType === 'รายเดือน' ? selectedRoom.monthly_price || 0
              : 0;
      setFormData(prev => ({ ...prev, price: price.toString() }));
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) setPreviewUrl(URL.createObjectURL(file));
  };

  const calculateTotalPrice = () => {
    const basePrice = parseInt(formData.price) || 0;
    const packageTotal = packageData.reduce((sum, item) => sum + (parseInt(item.price) || 0), 0);
    const medicalTotal = medicalData.reduce((sum, item) => sum + (parseInt(item.price) || 0), 0);
    const contractTotal = contractData.reduce((sum, item) => sum + (parseInt(item.price) || 0), 0);
    return basePrice + packageTotal + medicalTotal + contractTotal;
  };

  const openModal = (type) => {
    setModalType(type);
    setModalForm({ name: '', category: '', price: '' });
    setEditingIndex(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalForm({ name: '', category: '', price: '' });
    setEditingIndex(null);
  };

  const handleModalInputChange = (e) => {
    const { name, value } = e.target;
    setModalForm(prev => ({ ...prev, [name]: value }));
  };

  const handleModalSubmit = (e) => {
    e.preventDefault();
    const newItem = {
      name: modalForm.name,
      ...(modalType === 'contract' && { category: modalForm.category }),
      price: parseInt(modalForm.price),
    };
    if (editingIndex !== null) {
      if (modalType === 'package') setPackageData(prev => prev.map((item, i) => i === editingIndex ? { ...item, ...newItem } : item));
      else if (modalType === 'medical') setMedicalData(prev => prev.map((item, i) => i === editingIndex ? { ...item, ...newItem } : item));
      else if (modalType === 'contract') setContractData(prev => prev.map((item, i) => i === editingIndex ? { ...item, ...newItem } : item));
    } else {
      if (modalType === 'package') setPackageData(prev => [...prev, newItem]);
      else if (modalType === 'medical') setMedicalData(prev => [...prev, newItem]);
      else if (modalType === 'contract') setContractData(prev => [...prev, newItem]);
    }
    closeModal();
  };

  // ✅ Handler สำหรับปุ่ม Admission Medication Reconciliation
  const handleExportAdmissionMed = () => {
    try {
      // ดึง reconciliation data จาก state (ต้อง fetch แยกจาก /reconciliations/:id)
      const recData = serviceData; // state ที่ fetch มา

      const patient = {
        patient_name: recData.patient_name,       // "นางปวีณา ภูมิฤทธิกุล"
        hn: formData.hn,                          // จาก formData เดิม
        age: formData.age,
        room_number: recData.room_number,          // "308"
        ward_name: recData.ward_name,              // "ชั้น 3"
        registration_date: recData.reconciliation_date,

        // ✅ field ตรงกับ generateMedicationRows เลย ไม่ต้อง remap มาก
        medications: recData.medications.map(med => ({
          medicationName: med.medication_name,
          genericName: med.generic_name,
          tradeName: med.trade_name,
          externalHospital: med.external_hospital,
          isExternal: !!med.external_hospital,
          dosage: med.dosage,
          route: med.route,
          dosageInstruction: med.dosage_instruction,
          frequency: med.frequency,
          timing: med.timing,
          prnCondition: med.prn_condition,
          quantity: med.quantity,
          scheduleTimeDisplay: med.schedule_time_display,
          specialInstruction: med.special_instruction,
          status: med.status,
          lastDose: '',   // ไม่มีใน API นี้
          adjustedDosage: '',
          changeReason: '',
        })),
      };

      generateAdmissionMedicationReconciliation(patient);
    } catch (err) {
      setError(err.message);
    }
  };

  const exportToWord = async (formData, templatePath, setError) => {
    try {
      const now = new Date();
      const dateNow = formatThaiDate(now);
      const timeNow = formatThaiTime(now);
      const effectiveDate = formatWesternDate(now);

      let barcodeData = null;
      if (formData.hn) {
        const base64 = await generateBarcode(formData.hn);
        if (base64) barcodeData = { data: base64, opts: { width: 100, height: 50 } };
      }

      let totalMonths = '';
      let totalDays = '';
      if (formData.date && formData.toDate) {
        const result = calculateMonthsAndDays(formData.date, formData.toDate);
        totalMonths = result.months.toString();
        totalDays = result.days.toString();
      }

      const response = await fetch(templatePath);
      if (!response.ok) throw new Error(`Failed to load template: ${response.status}`);
      const arrayBuffer = await response.arrayBuffer();

      const zip = new PizZip(arrayBuffer);
      const doc = new Docxtemplater(zip, { linebreaks: true, delimiters: { start: '{{', end: '}}' } });

      const patientInfo = processPatientName({
        prename: formData.prename,
        firstName: formData.firstName,
        lastName: formData.lastName,
        gender: formData.gender,
      });

      doc.setData({
        hn: formData.hn || '',
        an: formData.an || '',
        prename: formData.prename || '',
        firstName: formData.firstName || '',
        lastName: formData.lastName || '',
        birthDate: formData.birth_date || '..........................................',
        age: formData.age ? formData.age.toString() : '................................................',
        idCard: formData.idNumber || '.............',
        address: formData.address || '...................',
        village: formData.village || '....................',
        subDistrict: formData.subDistrict || '....................',
        district: formData.district || '....................',
        province: formData.province || '....................',
        phone: formData.phone || '....................',
        email: formData.email || '....................',
        lineId: formData.lineId || '....................',
        relationship: formData.relationship || '....................',
        patientName: formData.patientName || '....................',
        patientAge: formData.patientAge || '....................',
        patientIdCard: formData.patientIdCard || '...............................................................',
        startDate: formData.date || '',
        endDate: formData.toDate || '',
        totalMonths: totalMonths || '',
        totalDays: totalDays || '',
        roomType: formData.floor || '',
        roomNumber: formData.building || '',
        serviceRate: formData.serviceRate || '....................',
        nursingRate: formData.nursingRate || '....................',
        medicalSupplies: formData.medicalSupplies || '...........................................................................................................................................................',
        doctorVisitRate: formData.doctorVisitRate || '........................',
        initialExamFee: formData.initialExamFee || '........................',
        totalServiceFee: formData.totalServiceFee || '........................',
        extraDoctorVisitFee: formData.extraDoctorVisitFee || '........................',
        finalGender: patientInfo.finalGender,
        dateNow,
        timeNow,
        dateTimeNow: `${dateNow} ${timeNow}`,
        effectiveDate,
      });

      doc.render();
      const blob = doc.getZip().generate({ type: 'blob' });
      saveAs(blob, `สัญญา_${formData.firstName || 'contract'}_${formData.lastName || 'document'}.docx`);
      setError(null);
      alert('Export Word สำเร็จ!');
    } catch (error) {
      console.error('Error exporting to Word:', error);
      setError(`เกิดข้อผิดพลาดในการ export Word: ${error.message}`);
      alert(`Error: ${error.message}`);
    }
  };

  const formatWesternDate = (date) => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  const formatThaiTime = (date) => {
    return [date.getHours(), date.getMinutes(), date.getSeconds()]
      .map(n => n.toString().padStart(2, '0'))
      .join(':');
  };

  const handleEdit = (type, index) => {
    let item;
    if (type === 'package') item = packageData[index];
    else if (type === 'medical') item = medicalData[index];
    else if (type === 'contract') item = contractData[index];
    setModalType(type);
    setEditingIndex(index);
    setModalForm({ name: item.name, category: item.category || '', price: item.price.toString() });
    setShowModal(true);
  };

  const handleDelete = (type, index) => {
    if (type === 'package') setPackageData(prev => prev.filter((_, i) => i !== index));
    else if (type === 'medical') setMedicalData(prev => prev.filter((_, i) => i !== index));
    else if (type === 'contract') setContractData(prev => prev.filter((_, i) => i !== index));
  };

  const handleMainSaveClick = () => setShowConfirmationModal(true);

  const handleConfirmSave = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!patient_id) throw new Error('❌ กรุณาเลือกผู้ป่วยก่อน');
      if (!formData.hn) throw new Error('❌ กรุณากรอก HN');
      if (!formData.an) throw new Error('❌ กรุณากรอก AN');
      if (!formData.date) throw new Error('❌ กรุณาเลือกวันที่เริ่มต้น');
      if (!formData.toDate) throw new Error('❌ กรุณาเลือกวันที่สิ้นสุด');
      if (!formData.building) throw new Error('❌ กรุณาเลือกห้อง');
      if (!formData.price || parseInt(formData.price) <= 0) throw new Error('❌ กรุณากรอกราคาที่มากกว่า 0');

      const strokeDept = departments.find(d => d.code === 'STROKE');
      if (!strokeDept) throw new Error('❌ ไม่พบแผนก STROKE ในระบบ');

      const basePrice = parseInt(formData.price) || 0;
      const totalPrice = calculateTotalPrice();

      const payload = {
        patient_id,
        department_id: strokeDept.id,
        patient_type: 'AN',
        profile_image: previewUrl || null,
        contract_data: {
          start_date: formData.date,
          end_date: formData.toDate,
          room_number: formData.building,
          billing_type: formData.floor,
          base_price: basePrice,
          total_price: totalPrice,
          notes: null,
          packages: packageData.map(pkg => ({
            id: pkg.packageId || pkg.id,
            name: pkg.name,
            original_price: parseInt(pkg.originalPrice || pkg.price) || 0,
            discount_type: pkg.discount?.type || null,
            discount_value: parseInt(pkg.discount?.value) || 0,
            final_price: parseInt(pkg.price) || 0,
          })),
          medical_supplies: medicalData.map(med => ({
            id: med.medicalId || med.id,
            name: med.name,
            original_price: parseInt(med.originalPrice || med.price) || 0,
            discount_type: med.discount?.type || null,
            discount_value: parseInt(med.discount?.value) || 0,
            final_price: parseInt(med.price) || 0,
          })),
          contract_items: contractData.map(con => ({
            id: con.contractId || con.id,
            name: con.name,
            category: con.category || '',
            original_price: parseInt(con.originalPrice || con.price) || 0,
            discount_type: con.discount?.type || null,
            discount_value: parseInt(con.discount?.value) || 0,
            final_price: parseInt(con.price) || 0,
          })),
        },
      };

      const response = await api.put(`/service-registrations/${service_id}`, payload);
      console.log('✅ SUCCESS RESPONSE:', response.data);

      alert('✅ แก้ไขข้อมูลสำเร็จ!');
      setShowConfirmationModal(false);
      setTimeout(() => navigate('/stroke-center'), 1000);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || err.message || 'เกิดข้อผิดพลาดในการบันทึก');
    } finally {
      setLoading(false);
    }
  };

  if (departmentsLoading && !formData.hn) {
    return (
      <div className="flex min-h-screen bg-slate-100 items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">กำลังโหลดข้อมูลแผนก...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-100">
      <div className="flex-1 p-8">
        <div className="bg-white p-8 rounded-lg shadow-md">
          {/* Header + Export Buttons */}
          <div className="flex justify-between items-center gap-4 mb-6">
            <h1 className="text-2xl font-bold text-gray-800 mr-auto">
              แก้ไขข้อมูลผู้รับบริการ (VN)
            </h1>

            <div className="flex items-center space-x-4">
              <button
                onClick={() => exportToWord(formData, '/templates/CODE1.docx', setError)}
                className="flex items-center justify-center space-x-2 px-4 sm:px-6 py-2 sm:py-2.5 text-sm font-medium bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:shadow-lg transition-all duration-200 transform hover:scale-105"
              >
                <FileText className="w-4 h-4" />
                <span>สัญญาจ้างทางการแพทย์</span>
              </button>
              <button
                onClick={() => exportToWord(formData, '/templates/สัญญาเงื่อนไขและข้อตกลงการซื้อคอร์ส.docx', setError)}
                className="flex items-center justify-center space-x-2 px-4 sm:px-6 py-2 sm:py-2.5 text-sm font-medium bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:shadow-lg transition-all duration-200 transform hover:scale-105"
              >
                <FileText className="w-4 h-4" />
                <span>สัญญาเงื่อนไขและข้อตกลงการซื้อคอร์ส</span>
              </button>
              <button
                onClick={() => exportToWord(formData, '/templates/เอกสารAdmit.docx', setError)}
                className="flex items-center justify-center space-x-2 px-4 sm:px-6 py-2 sm:py-2.5 text-sm font-medium bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:shadow-lg transition-all duration-200 transform hover:scale-105"
              >
                📄 เอกสาร Admit
              </button>
              <button
                onClick={() => exportToWord(formData, '/templates/เอกสารแนบสัญญา.docx', setError)}
                className="flex items-center justify-center space-x-2 px-4 sm:px-6 py-2 sm:py-2.5 text-sm font-medium bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:shadow-lg transition-all duration-200 transform hover:scale-105"
              >
                📄 เอกสารแนบสัญญา
              </button>
              <button
                onClick={() => exportToWord(formData, '/templates/เอกสารผู้ป่วยโรคหลอดเลือดสมอง.docx', setError)}
                className="flex items-center justify-center space-x-2 px-4 sm:px-6 py-2 sm:py-2.5 text-sm font-medium bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:shadow-lg transition-all duration-200 transform hover:scale-105"
              >
                📄 เอกสารผู้ป่วยโรคหลอดเลือดสมอง
              </button>

              {/* ✅ ปุ่มใหม่ — Admission Medication Reconciliation */}
              <button
                onClick={handleExportAdmissionMed}
                className="flex items-center justify-center space-x-2 px-4 sm:px-6 py-2 sm:py-2.5 text-sm font-medium bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:shadow-lg transition-all duration-200 transform hover:scale-105"
              >
                <FileText className="w-4 h-4" />
                <span>Admission Med Reconciliation</span>
              </button>


              <button
                onClick={() => {
                  try {
                    generateMonthlyMedicationWord({
                      patient_name: serviceData?.patient_name,
                      room_number: serviceData?.room_number,
                      ward_name: serviceData?.ward_name,
                      medications: serviceData?.medications?.map(med => ({
                        medicationName: med.medication_name,
                        dosage: med.dosage,
                        dosageInstruction: med.dosage_instruction,
                        specialInstruction: med.special_instruction,
                        timing: med.timing,
                        scheduleTime: med.schedule_time_display?.split(',').map(t => t.trim()) ?? [],
                      })) ?? [],
                    }, new Date());
                  } catch (err) {
                    setError(err.message);
                  }
                }}
                className="flex items-center justify-center space-x-2 px-4 sm:px-6 py-2 sm:py-2.5 text-sm font-medium bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-xl hover:shadow-lg transition-all duration-200 transform hover:scale-105"
              >
                <FileText className="w-4 h-4" />
                <span>บันทึกการให้ยารายเดือน</span>
              </button>

              <button
                onClick={() => {
                  try {
                    generatePhysicianOrderSheet({
                      patient_name: serviceData?.patient_name,
                      hn: formData.hn,
                      age: formData.age,
                      room_number: serviceData?.room_number,
                      ward_name: serviceData?.ward_name,
                      registration_date: serviceData?.reconciliation_date,
                      medications: serviceData?.medications ?? [],
                    });
                  } catch (err) {
                    setError(err.message);
                  }
                }}
                className="flex items-center justify-center space-x-2 px-4 sm:px-6 py-2 sm:py-2.5 text-sm font-medium bg-gradient-to-r from-purple-600 to-violet-600 text-white rounded-xl hover:shadow-lg transition-all duration-200 transform hover:scale-105"
              >
                <FileText className="w-4 h-4" />
                <span>Physician's Order Sheet</span>
              </button>
            </div>
          </div>

          {/* Search Section */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              ค้นหาผู้ป่วย (HN หรือ ชื่อ-นามสกุล)
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleUniversalSearch()}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="พิมพ์ HN, ชื่อ หรือ นามสกุล..."
              />
              <button
                onClick={handleUniversalSearch}
                disabled={isSearching || !searchQuery || searchQuery.trim().length < 2}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSearching ? (
                  <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div><span>กำลังค้นหา...</span></>
                ) : (
                  <><Search className="w-4 h-4" /><span>ค้นหา</span></>
                )}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">* สามารถค้นหาด้วย HN, ชื่อ หรือ นามสกุล (อย่างน้อย 2 ตัวอักษร)</p>
          </div>

          {/* Error Alert */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600">เกิดข้อผิดพลาด: {error}</p>
              <button onClick={() => setError(null)} className="mt-2 text-sm text-red-500 hover:text-red-700">ปิดข้อความ</button>
            </div>
          )}

          {/* Discharge Status Section */}
          <div className="mb-6 p-4 border-2 rounded-lg" style={{
            borderColor: discharged ? '#dc2626' : '#10b981',
            backgroundColor: discharged ? '#fee2e2' : '#f0fdf4',
          }}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <LogOut className={`w-6 h-6 ${discharged ? 'text-red-600' : 'text-green-600'}`} />
                <div>
                  <p className="text-sm font-medium text-gray-700">สถานะผู้ป่วย</p>
                  <p className={`text-lg font-bold ${discharged ? 'text-red-600' : 'text-green-600'}`}>
                    {discharged ? '❌ ออกจากศูนย์แล้ว' : '✅ อยู่ในศูนย์'}
                  </p>
                  {discharged && formData.toDate && (
                    <p className="text-sm text-gray-600 mt-1">วันจำหน่าย: {formData.toDate}</p>
                  )}
                </div>
              </div>
              <button
                onClick={() => {
                  if (!discharged) { setShowDischargeModal(true); }
                  else if (window.confirm('ต้องการยกเลิกการจำหน่ายผู้ป่วยหรือไม่?')) { handleUndischarge(); }
                }}
                disabled={dischargeLoading}
                className={`px-6 py-3 font-semibold rounded-lg text-white transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${discharged ? 'bg-orange-600 hover:bg-orange-700' : 'bg-green-600 hover:bg-green-700'}`}
              >
                <LogOut className="w-4 h-4" />
                {discharged ? 'ยกเลิกการจำหน่าย' : 'จำหน่ายผู้ป่วย'}
              </button>
            </div>
          </div>

          {/* Patient Info Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="flex flex-col items-center justify-center p-4 border border-dashed border-gray-300 rounded-lg h-full">
              <input type="file" id="imageUpload" accept="image/*" onChange={handleFileSelect} className="hidden" />
              <label htmlFor="imageUpload" className="cursor-pointer flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors">
                {previewUrl ? (
                  <img src={previewUrl} alt="Preview" className="w-full h-full object-cover rounded-lg" />
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-gray-400 mb-2" />
                    <p className="text-sm text-blue-500 hover:text-blue-600 font-medium">Click to upload</p>
                    <p className="text-sm text-gray-500">or drag and drop</p>
                    <p className="text-xs text-gray-400 mt-2">JPG, JPEG, PNG</p>
                  </>
                )}
              </label>
            </div>

            <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { label: 'HN', name: 'hn', readOnly: true },
                { label: 'AN', name: 'an', readOnly: true },
                { label: 'ชื่อ', name: 'firstName' },
                { label: 'นามสกุล', name: 'lastName' },
                { label: 'เลขบัตรประชาชน', name: 'idNumber' },
              ].map(({ label, name, readOnly }) => (
                <div key={name} className="flex flex-col">
                  <label className="text-sm font-medium text-gray-700 mb-2">{label}</label>
                  <input
                    type="text"
                    name={name}
                    value={formData[name]}
                    onChange={handleInputChange}
                    readOnly={readOnly}
                    className={`px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${readOnly ? 'bg-gray-50' : ''}`}
                  />
                </div>
              ))}

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-2">วันเกิด</label>
                <input type="date" name="birth_date" value={formData.birth_date} onChange={handleInputChange}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-2">อายุ</label>
                <input type="number" name="age" value={formData.age} onChange={handleInputChange}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-2">คลินิก</label>
                <input type="text" value="ศูนย์ฟื้นฟูผู้ป่วยโรคหลอดเลือดสมองและระบบประสาท"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 focus:outline-none" readOnly />
              </div>
            </div>
          </div>

          {/* Contract Duration */}
          <div className="mt-8">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">ระยะเวลาของสัญญา</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-2">วันที่</label>
                <input type="date" name="date" value={formData.date} onChange={handleInputChange}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-2">ถึงวันที่</label>
                <input type="date" name="toDate" value={formData.toDate} onChange={handleInputChange}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-2">ห้อง</label>
                <div className="relative">
                  <select name="building" value={selectedRoom?.room_number || ''} onChange={handleRoomChange}
                    disabled={roomsLoading}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white">
                    <option value="">เลือกห้อง</option>
                    {rooms.map(room => (
                      <option key={room.id} value={room.room_number}>{room.room_number} - {room.room_type}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                </div>
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-2">ประเภท</label>
                <div className="relative">
                  <select name="floor" value={formData.floor} onChange={handleBillingTypeChange}
                    disabled={!selectedRoom}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white disabled:bg-gray-100 disabled:cursor-not-allowed">
                    <option value="รายวัน">รายวัน</option>
                    {selectedRoom?.weekly_price && <option value="รายสัปดาห์">รายสัปดาห์</option>}
                    {selectedRoom?.monthly_price && <option value="รายเดือน">รายเดือน</option>}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                </div>
              </div>

              <div className="flex flex-col">
                <label className="text-sm font-medium text-gray-700 mb-2">ราคา</label>
                <input type="number" name="price" value={formData.price} onChange={handleInputChange}
                  disabled={!selectedRoom}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="0" />
              </div>
            </div>

            {selectedRoom && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-2">ข้อมูลห้องที่เลือก</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div><span className="text-gray-600">หมายเลขห้อง:</span><p className="font-medium">{selectedRoom.room_number}</p></div>
                  <div><span className="text-gray-600">ประเภทห้อง:</span><p className="font-medium">{selectedRoom.room_type}</p></div>
                  {selectedRoom.daily_price && <div><span className="text-gray-600">รายวัน:</span><p className="font-medium text-green-600">{selectedRoom.daily_price.toLocaleString()} บาท</p></div>}
                  {selectedRoom.monthly_price && <div><span className="text-gray-600">รายเดือน:</span><p className="font-medium text-green-600">{selectedRoom.monthly_price.toLocaleString()} บาท</p></div>}
                </div>
              </div>
            )}
          </div>

          {/* Tables Section */}
          <div className="mt-8 space-y-8">
            {/* Packages */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-800">คอร์สแพ็คเกจกายภาพ</h2>
                <div className="flex gap-2">
                  <button onClick={() => setShowPackageModal(true)}
                    className="bg-purple-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2">
                    <Package className="w-4 h-4" />เลือกจากแพ็คเกจ
                  </button>
                  <button onClick={() => openModal('package')}
                    className="bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2">
                    <Plus className="w-4 h-4" />เพิ่มเอง
                  </button>
                </div>
              </div>
              <div className="border border-blue-300 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-blue-50">
                    <tr>
                      {['ลำดับ', 'ชื่อแพคเกจ', 'ราคา', 'จัดการ'].map((h, i) => (
                        <th key={h} className={`px-4 py-3 text-left text-sm font-medium text-gray-700 ${i < 3 ? 'border-r border-blue-300' : ''}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {packageData.length === 0 ? (
                      <tr><td colSpan="4" className="px-4 py-8 text-center text-gray-500 bg-gray-50">ไม่มีข้อมูล</td></tr>
                    ) : packageData.map((item, index) => (
                      <tr key={item.id} className="border-t border-blue-300">
                        <td className="px-4 py-3 border-r border-blue-300">{index + 1}</td>
                        <td className="px-4 py-3 border-r border-blue-300">{item.name}</td>
                        <td className="px-4 py-3 border-r border-blue-300">{item.price.toLocaleString()}</td>
                        <td className="px-4 py-3"><div className="flex gap-2">
                          <button onClick={() => handleEdit('package', index)} className="text-blue-600 hover:text-blue-800"><Edit className="w-4 h-4" /></button>
                          <button onClick={() => handleDelete('package', index)} className="text-red-600 hover:text-red-800"><Trash2 className="w-4 h-4" /></button>
                        </div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Medical */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-800">รายการเหมาเวชภัณฑ์</h2>
                <div className="flex gap-2">
                  <button onClick={() => setShowMedicalModal(true)}
                    className="bg-green-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2">
                    <Package className="w-4 h-4" />เลือกจากรายการ
                  </button>
                  <button onClick={() => openModal('medical')}
                    className="bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2">
                    <Plus className="w-4 h-4" />เพิ่มเอง
                  </button>
                </div>
              </div>
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      {['ลำดับ', 'ชื่อรายการ', 'ราคา', 'จัดการ'].map((h, i) => (
                        <th key={h} className={`px-4 py-3 text-left text-sm font-medium text-gray-700 ${i < 3 ? 'border-r border-gray-300' : ''}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {medicalData.length === 0 ? (
                      <tr><td colSpan="4" className="px-4 py-8 text-center text-gray-500 bg-gray-50">ไม่มีข้อมูล</td></tr>
                    ) : medicalData.map((item, index) => (
                      <tr key={item.id} className="border-t border-gray-300">
                        <td className="px-4 py-3 border-r border-gray-300">{index + 1}</td>
                        <td className="px-4 py-3 border-r border-gray-300">{item.name}</td>
                        <td className="px-4 py-3 border-r border-gray-300">{item.price.toLocaleString()}</td>
                        <td className="px-4 py-3"><div className="flex gap-2">
                          <button onClick={() => handleEdit('medical', index)} className="text-blue-600 hover:text-blue-800"><Edit className="w-4 h-4" /></button>
                          <button onClick={() => handleDelete('medical', index)} className="text-red-600 hover:text-red-800"><Trash2 className="w-4 h-4" /></button>
                        </div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Contracts */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-800">รายการเหมา</h2>
                <div className="flex gap-2">
                  <button onClick={() => setShowContractModal(true)}
                    className="bg-purple-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2">
                    <Package className="w-4 h-4" />เลือกจากรายการ
                  </button>
                  <button onClick={() => openModal('contract')}
                    className="bg-blue-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2">
                    <Plus className="w-4 h-4" />เพิ่มเอง
                  </button>
                </div>
              </div>
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      {['ลำดับ', 'ชื่อรายการ', 'หมายเหตุ', 'ราคา', 'จัดการ'].map((h, i) => (
                        <th key={h} className={`px-4 py-3 text-left text-sm font-medium text-gray-700 ${i < 4 ? 'border-r border-gray-300' : ''}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {contractData.length === 0 ? (
                      <tr><td colSpan="5" className="px-4 py-8 text-center text-gray-500 bg-gray-50">ไม่มีข้อมูล</td></tr>
                    ) : contractData.map((item, index) => (
                      <tr key={item.id} className="border-t border-gray-300">
                        <td className="px-4 py-3 border-r border-gray-300">{index + 1}</td>
                        <td className="px-4 py-3 border-r border-gray-300">{item.name}</td>
                        <td className="px-4 py-3 border-r border-gray-300">{item.category}</td>
                        <td className="px-4 py-3 border-r border-gray-300">{item.price.toLocaleString()}</td>
                        <td className="px-4 py-3"><div className="flex gap-2">
                          <button onClick={() => handleEdit('contract', index)} className="text-blue-600 hover:text-blue-800"><Edit className="w-4 h-4" /></button>
                          <button onClick={() => handleDelete('contract', index)} className="text-red-600 hover:text-red-800"><Trash2 className="w-4 h-4" /></button>
                        </div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Save/Cancel */}
          <div className="flex justify-end space-x-4 mt-8">
            <button onClick={handleMainSaveClick} disabled={loading}
              className="bg-green-600 text-white font-semibold py-2 px-6 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed">
              {loading ? 'กำลังบันทึก...' : 'บันทึกข้อมูล'}
            </button>
            <button onClick={() => navigate(-1)}
              className="bg-red-600 text-white font-semibold py-2 px-6 rounded-lg hover:bg-red-700 transition-colors">
              ยกเลิก
            </button>
          </div>
        </div>
      </div>

      {/* ===== Modals ===== */}

      {/* Discharge Modal */}
      {showDischargeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">จำหน่ายผู้ป่วย</h3>
              <button onClick={() => setShowDischargeModal(false)} className="text-gray-500 hover:text-gray-700"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">วันที่จำหน่าย</label>
                <input type="date" value={dischargeDate} onChange={(e) => setDischargeDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">หมายเหตุ (ไม่จำเป็น)</label>
                <textarea value={dischargeNotes} onChange={(e) => setDischargeNotes(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  rows="3" placeholder="กรอกหมายเหตุเพิ่มเติม..." />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button onClick={() => setShowDischargeModal(false)} className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">ยกเลิก</button>
              <button onClick={handleDischarge} disabled={dischargeLoading || !dischargeDate}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed">
                {dischargeLoading ? 'กำลังบันทึก...' : 'ยืนยันการจำหน่าย'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search Results Modal */}
      {showSearchModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
              <h2 className="text-2xl font-bold">ผลการค้นหาผู้ป่วย</h2>
              <button onClick={() => { setShowSearchModal(false); setSearchResults([]); }} className="p-2 hover:bg-gray-100 rounded-lg"><X className="h-6 w-6" /></button>
            </div>
            <div className="p-6">
              {searchResults.length === 0 ? (
                <div className="text-center py-8"><p className="text-gray-500">ไม่พบผู้ป่วยที่ค้นหา</p></div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600 mb-4">พบผู้ป่วย {searchResults.length} รายการ</p>
                  {searchResults.map((patient, index) => (
                    <div key={patient.id || index} onClick={() => selectPatientFromSearch(patient)}
                      className="border border-gray-200 rounded-lg p-4 hover:border-blue-500 hover:bg-blue-50 cursor-pointer transition-all">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            {patient.profile_image && <img src={patient.profile_image} alt="Profile" className="w-12 h-12 rounded-full object-cover" />}
                            <div>
                              <h3 className="font-semibold text-lg text-gray-900">{patient.first_name} {patient.last_name}</h3>
                              <p className="text-sm text-blue-600">HN: {patient.hn}</p>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 text-sm mt-3">
                            <div><span className="text-gray-500">เลขบัตรประชาชน:</span><p className="font-medium">{patient.id_card || '-'}</p></div>
                            <div><span className="text-gray-500">เพศ:</span><p className="font-medium">{patient.gender || '-'}</p></div>
                          </div>
                        </div>
                        <button className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex-shrink-0">เลือก</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="sticky bottom-0 bg-white border-t p-6 flex justify-end">
              <button onClick={() => { setShowSearchModal(false); setSearchResults([]); }} className="px-6 py-2 border rounded-lg hover:bg-gray-50">ปิด</button>
            </div>
          </div>
        </div>
      )}

      {/* Package Selection Modal */}
      {showPackageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
              <h2 className="text-2xl font-bold">เลือกแพ็คเกจ</h2>
              <button onClick={() => setShowPackageModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="h-6 w-6" /></button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {availablePackages.map(pkg => (
                  <div key={pkg.id} onClick={() => setSelectedPackageForPatient(pkg)}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${selectedPackageForPatient?.id === pkg.id ? 'border-purple-600 bg-purple-50' : 'border-gray-200 hover:border-purple-300'}`}>
                    <h3 className="font-semibold text-gray-900">{pkg.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">{pkg.description}</p>
                    <div className="mt-3 text-lg font-bold text-green-600">{pkg.price?.toLocaleString()} บาท</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t p-6 flex justify-end gap-4">
              <button onClick={() => setShowPackageModal(false)} className="px-6 py-3 border rounded-lg hover:bg-gray-50">ยกเลิก</button>
              <button
                onClick={() => {
                  if (selectedPackageForPatient) {
                    setPackageData(prev => [...prev, { id: Date.now(), name: selectedPackageForPatient.name, price: selectedPackageForPatient.price, originalPrice: selectedPackageForPatient.price, packageId: selectedPackageForPatient.id }]);
                    setShowPackageModal(false);
                    setSelectedPackageForPatient(null);
                  }
                }}
                disabled={!selectedPackageForPatient}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50">
                เพิ่มแพ็คเกจ
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Medical Selection Modal */}
      {showMedicalModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
              <h2 className="text-2xl font-bold">เลือกเวชภัณฑ์</h2>
              <button onClick={() => setShowMedicalModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="h-6 w-6" /></button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {availableMedical.map(item => (
                  <div key={item.id} onClick={() => setSelectedMedical(item)}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${selectedMedical?.id === item.id ? 'border-green-600 bg-green-50' : 'border-gray-200 hover:border-green-300'}`}>
                    <h3 className="font-semibold text-gray-900">{item.name}</h3>
                    <div className="mt-3 text-lg font-bold text-green-600">{item.price?.toLocaleString()} บาท</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t p-6 flex justify-end gap-4">
              <button onClick={() => setShowMedicalModal(false)} className="px-6 py-3 border rounded-lg hover:bg-gray-50">ยกเลิก</button>
              <button
                onClick={() => {
                  if (selectedMedical) {
                    setMedicalData(prev => [...prev, { id: Date.now(), name: selectedMedical.name, price: selectedMedical.price, originalPrice: selectedMedical.price, medicalId: selectedMedical.id }]);
                    setShowMedicalModal(false);
                    setSelectedMedical(null);
                  }
                }}
                disabled={!selectedMedical}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                เพิ่มรายการ
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contract Selection Modal */}
      {showContractModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
              <h2 className="text-2xl font-bold">เลือกรายการเหมา</h2>
              <button onClick={() => setShowContractModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="h-6 w-6" /></button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {availableContracts.map(item => (
                  <div key={item.id} onClick={() => setSelectedContract(item)}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all ${selectedContract?.id === item.id ? 'border-purple-600 bg-purple-50' : 'border-gray-200 hover:border-purple-300'}`}>
                    <h3 className="font-semibold text-gray-900">{item.name}</h3>
                    <div className="mt-3 text-lg font-bold text-purple-600">{item.price?.toLocaleString()} บาท</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="sticky bottom-0 bg-white border-t p-6 flex justify-end gap-4">
              <button onClick={() => setShowContractModal(false)} className="px-6 py-3 border rounded-lg hover:bg-gray-50">ยกเลิก</button>
              <button
                onClick={() => {
                  if (selectedContract) {
                    setContractData(prev => [...prev, { id: Date.now(), name: selectedContract.name, category: selectedContract.category || selectedContract.billing, price: selectedContract.price, originalPrice: selectedContract.price, contractId: selectedContract.id }]);
                    setShowContractModal(false);
                    setSelectedContract(null);
                  }
                }}
                disabled={!selectedContract}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50">
                เพิ่มรายการ
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Item Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                {editingIndex !== null ? 'แก้ไข' : 'เพิ่ม'}
                {modalType === 'package' && ' คอร์สแพ็คเกจกายภาพ'}
                {modalType === 'medical' && ' รายการเหมาเวชภัณฑ์'}
                {modalType === 'contract' && ' รายการเหมา'}
              </h3>
              <button onClick={closeModal} className="text-gray-500 hover:text-gray-700"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleModalSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">ชื่อรายการ</label>
                  <input type="text" name="name" value={modalForm.name} onChange={handleModalInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                </div>
                {modalType === 'contract' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">หมายเหตุ</label>
                    <input type="text" name="category" value={modalForm.category} onChange={handleModalInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">ราคา</label>
                  <input type="number" name="price" value={modalForm.price} onChange={handleModalInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button type="button" onClick={closeModal} className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">ยกเลิก</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  {editingIndex !== null ? 'บันทึก' : 'เพิ่ม'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b pb-3 mb-4">
              <h3 className="text-xl font-bold text-gray-800">โปรดตรวจสอบข้อมูลก่อนบันทึก</h3>
              <button onClick={() => setShowConfirmationModal(false)} className="text-gray-500 hover:text-gray-800"><X className="w-6 h-6" /></button>
            </div>
            <div className="space-y-4 text-sm">
              <h4 className="font-semibold text-lg text-blue-700">ข้อมูลผู้รับบริการ</h4>
              <p><strong>HN:</strong> {formData.hn}</p>
              <p><strong>AN:</strong> {formData.an}</p>
              <p><strong>ชื่อ-นามสกุล:</strong> {formData.firstName} {formData.lastName}</p>
              <p><strong>วันเกิด:</strong> {formData.birth_date}</p>
              <p><strong>อายุ:</strong> {formData.age} ปี</p>
              <p><strong>คลินิก:</strong> ศูนย์ฟื้นฟูผู้ป่วยโรคหลอดเลือดสมองและระบบประสาท</p>
              <h4 className="font-semibold text-lg text-blue-700 mt-4 pt-4 border-t">ข้อมูลสัญญา</h4>
              <p><strong>วันที่:</strong> {formData.date} <strong>ถึงวันที่:</strong> {formData.toDate}</p>
              <p><strong>ห้อง:</strong> {formData.building} <strong>ประเภท:</strong> {formData.floor}</p>
              <p><strong>ราคาฐาน:</strong> {(parseInt(formData.price) || 0).toLocaleString()} บาท</p>
              <h4 className="font-semibold text-lg text-blue-700 mt-4 pt-4 border-t">คอร์สแพ็คเกจ</h4>
              {packageData.length > 0 ? (
                <ul className="list-disc pl-5">{packageData.map(item => <li key={item.id}>{item.name} - {item.price.toLocaleString()} บาท</li>)}</ul>
              ) : <p className="text-gray-500">ไม่มีรายการ</p>}
              <div className="mt-4 pt-4 border-t">
                <p className="font-semibold text-lg text-green-700">รวมทั้งหมด: {calculateTotalPrice().toLocaleString()} บาท</p>
              </div>
            </div>
            <div className="flex justify-end space-x-4 mt-6 pt-4 border-t">
              <button onClick={() => setShowConfirmationModal(false)}
                className="px-6 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200">แก้ไข</button>
              <button onClick={handleConfirmSave} disabled={loading}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400">
                {loading ? 'กำลังบันทึก...' : 'ยืนยันการบันทึก'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}